'use client';

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import type { Employee, EmployeeReview, EmployeeCertification, EmployeeWithStats, EmploymentTypeFilter, SortKey } from './types';
import EmployeeCard from './EmployeeCard';
import HrKpiStrip from './HrKpiStrip';
import HrDirectoryControls from './HrDirectoryControls';
import EmployeeDetailSlideover from './EmployeeDetailSlideover';
import NewEmployeeModal from './NewEmployeeModal';
import PendingApprovalsPanel from './PendingApprovalsPanel';

const HrDirectoryInteractive = () => {
  const { currentUser } = useAuth();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [reviews, setReviews] = useState<EmployeeReview[]>([]);
  const [certifications, setCertifications] = useState<EmployeeCertification[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<EmploymentTypeFilter>('active');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [selectedDepartment, setSelectedDepartment] = useState<string | null>(null);
  const [clockedIn, setClockedIn] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeWithStats | null>(null);
  const [newEmployeeModalOpen, setNewEmployeeModalOpen] = useState(false);
  const [currentUserEmployeeId, setCurrentUserEmployeeId] = useState<string | null>(null);
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);

  const supabase = createClient();

  const loadData = useCallback(async () => {
    try {
      const [empRes, revRes, certRes] = await Promise.all([
        supabase.from('employees').select('*').order('last_name'),
        supabase.from('employee_reviews').select('id, employee_id, status, overall_rating, review_period_end, released_at'),
        supabase.from('employee_certifications').select('id, employee_id, certification_name, status, expiration_date'),
      ]);
      if (empRes.error) throw empRes.error;
      if (revRes.error) throw revRes.error;
      if (certRes.error) throw certRes.error;

      setEmployees((empRes.data ?? []) as Employee[]);
      setReviews((revRes.data ?? []) as EmployeeReview[]);
      setCertifications((certRes.data ?? []) as EmployeeCertification[]);
    } catch (err) {
      console.error('Failed to load HR data:', err);
      setEmployees([]);
      setReviews([]);
      setCertifications([]);
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  // Debounce realtime refreshes — coalesce bursts of changes
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const debouncedReload = useCallback(() => {
    if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
    refreshTimeoutRef.current = setTimeout(() => {
      loadData();
    }, 500);
  }, [loadData]);

  useEffect(() => {
    loadData();
    const channel = supabase
      .channel('hr_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'employees' }, () => debouncedReload())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'employee_reviews' }, () => debouncedReload())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'employee_certifications' }, () => debouncedReload())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [loadData, debouncedReload, supabase]);

  useEffect(() => {
    if (!currentUser?.id) return;
    supabase
      .from('user_profiles')
      .select('employee_id')
      .eq('id', currentUser.id)
      .maybeSingle()
      .then(({ data }) => {
        setCurrentUserEmployeeId(data?.employee_id ?? null);
      });
  }, [currentUser?.id, supabase]);

  useEffect(() => {
    if (!currentUserEmployeeId) {
      setPendingApprovalsCount(0);
      return;
    }
    const fetchCount = async () => {
      const { count } = await supabase
        .from('employee_time_off_requests')
        .select('*', { count: 'exact', head: true })
        .eq('current_approver_id', currentUserEmployeeId)
        .eq('status', 'pending');
      setPendingApprovalsCount(count ?? 0);
    };
    fetchCount();
    const channel = supabase
      .channel('pto_count_kpi')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'employee_time_off_requests' }, () => fetchCount())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [currentUserEmployeeId, supabase]);

  // Compute per-employee stats by joining reviews + certifications
  const employeesWithStats = useMemo<EmployeeWithStats[]>(() => {
    const sixtyDaysFromNow = Date.now() + 60 * 24 * 60 * 60 * 1000;

    return employees.map(emp => {
      const empCerts = certifications.filter(c => c.employee_id === emp.id);
      const activeCertCount = empCerts.filter(c => c.status === 'active').length;
      const expiringCertCount = empCerts.filter(c => {
        if (c.status !== 'active' || !c.expiration_date) return false;
        const expTime = new Date(c.expiration_date).getTime();
        return expTime <= sixtyDaysFromNow;
      }).length;

      const empReviews = reviews.filter(r => r.employee_id === emp.id);
      const pendingReviewCount = empReviews.filter(r => r.status === 'draft').length;
      const releasedReviews = empReviews.filter(r => r.status === 'released' || r.status === 'acknowledged');
      const releasedReviewCount = releasedReviews.length;

      // Latest released review's rating
      const latestReleased = releasedReviews
        .slice()
        .sort((a, b) => new Date(b.released_at ?? b.review_period_end).getTime() - new Date(a.released_at ?? a.review_period_end).getTime())[0];
      const latestReviewRating = latestReleased?.overall_rating ?? null;

      return {
        ...emp,
        active_certs_count: activeCertCount,
        expiring_certs_count: expiringCertCount,
        pending_reviews_count: pendingReviewCount,
        released_reviews_count: releasedReviewCount,
        latest_review_rating: latestReviewRating,
      };
    });
  }, [employees, reviews, certifications]);

  // Departments — derived
  const departments = useMemo(
    () => Array.from(new Set(employees.map(e => e.department).filter(Boolean))).sort(),
    [employees]
  );

  // Filter + sort
  const visibleEmployees = useMemo(() => {
    let list = employeesWithStats;

    // Filter by employment type tab
    if (filter === 'active') {
      list = list.filter(e => !e.termination_date && e.employment_type !== 'terminated');
    } else if (filter === 'terminated') {
      list = list.filter(e => !!e.termination_date || e.employment_type === 'terminated');
    } else if (filter === 'contractor') {
      list = list.filter(e => e.employment_type === 'contractor');
    } else if (filter === 'on_leave') {
      // No data yet — empty for now
      list = [];
    }

    // Filter by department
    if (selectedDepartment) {
      list = list.filter(e => e.department === selectedDepartment);
    }

    // Search
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(e =>
        `${e.first_name} ${e.last_name}`.toLowerCase().includes(q)
        || (e.preferred_name ?? '').toLowerCase().includes(q)
        || (e.job_title ?? '').toLowerCase().includes(q)
        || (e.department ?? '').toLowerCase().includes(q)
        || (e.email ?? '').toLowerCase().includes(q)
      );
    }

    // Sort
    list = [...list].sort((a, b) => {
      switch (sortKey) {
        case 'name':
          return (a.last_name ?? '').localeCompare(b.last_name ?? '');
        case 'department':
          return ((a.department ?? '').localeCompare(b.department ?? '')) || ((a.last_name ?? '').localeCompare(b.last_name ?? ''));
        case 'hire_date': {
          // Longest tenure first = oldest hire date first
          const aHired = a.hire_date ? new Date(a.hire_date).getTime() : Infinity;
          const bHired = b.hire_date ? new Date(b.hire_date).getTime() : Infinity;
          return aHired - bHired;
        }
        case 'rating':
          return (b.latest_review_rating ?? -1) - (a.latest_review_rating ?? -1);
        default:
          return 0;
      }
    });

    return list;
  }, [employeesWithStats, filter, selectedDepartment, search, sortKey]);

  // PLACEHOLDER click handler — HR-3 will wire the slideover modal here
  const handleEmployeeClick = (employee: EmployeeWithStats) => {
    console.log('Employee clicked (HR-3 will open slideover):', employee.first_name, employee.last_name);
    setSelectedEmployee(employee);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ background: '#f8fafc' }}>
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 rounded-full border-4 border-[#E89A3C] border-t-transparent animate-spin" />
          <span className="text-sm text-gray-500">Loading HR Directory...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#f8fafc' }}>
      {/* Sticky Header */}
      <div className="sticky top-[60px] z-30 bg-white border-b border-gray-200 shadow-sm">
        <div className="flex items-center justify-between px-6 py-3">
          <div>
            <h1 className="text-xl font-bold" style={{ color: '#1C2E5E' }}>HR Directory</h1>
            <p className="text-xs text-gray-500 mt-0.5">
              {employees.length} employees · {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setNewEmployeeModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-white transition-all duration-200 bg-[#1C2E5E] hover:bg-[#022b80]"
              style={{ minHeight: 40 }}
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 7a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM19 8v6M22 11h-6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Add Employee
            </button>
            <button
              onClick={() => setClockedIn(p => !p)}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200"
              style={{
                background: clockedIn ? '#1C2E5E' : '#E89A3C',
                color: '#fff',
                minHeight: 40,
                boxShadow: '0 2px 8px rgba(232,154,60,0.3)',
              }}
            >
              <Icon name={clockedIn ? 'ClockIcon' : 'PlayCircleIcon'} size={16} />
              {clockedIn ? 'Clock Out' : 'Clock In'}
            </button>
            {currentUser && (
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold"
                style={{ background: '#1C2E5E' }}
              >
                {currentUser.first_name?.charAt(0) ?? '?'}{currentUser.last_name?.charAt(0) ?? '?'}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="px-6 py-6 max-w-[1600px] mx-auto">
        {/* KPI Strip */}
        <HrKpiStrip employees={employeesWithStats} pendingApprovalsCount={pendingApprovalsCount} />

        <PendingApprovalsPanel
          approverEmployeeId={currentUserEmployeeId}
          onResolved={() => {
            if (currentUserEmployeeId) {
              supabase
                .from('employee_time_off_requests')
                .select('*', { count: 'exact', head: true })
                .eq('current_approver_id', currentUserEmployeeId)
                .eq('status', 'pending')
                .then(({ count }) => setPendingApprovalsCount(count ?? 0));
            }
          }}
        />

        {/* Controls */}
        <HrDirectoryControls
          search={search}
          onSearchChange={setSearch}
          filter={filter}
          onFilterChange={setFilter}
          sortKey={sortKey}
          onSortChange={setSortKey}
          departments={departments}
          selectedDepartment={selectedDepartment}
          onDepartmentChange={setSelectedDepartment}
          visibleCount={visibleEmployees.length}
          totalCount={employeesWithStats.length}
        />

        {/* Grid */}
        {visibleEmployees.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center" style={{ marginTop: 8 }}>
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
              <Icon name="UserGroupIcon" size={24} className="text-gray-400" />
            </div>
            <p className="text-sm font-semibold text-gray-700">No employees match your filters</p>
            <p className="text-[12px] text-gray-500 mt-1">Try adjusting search or clearing the department/filter selection.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {visibleEmployees.map(emp => (
              <EmployeeCard
                key={emp.id}
                employee={emp}
                onClick={handleEmployeeClick}
              />
            ))}
          </div>
        )}
      </div>

      <EmployeeDetailSlideover
        employee={selectedEmployee}
        onClose={() => setSelectedEmployee(null)}
        allEmployees={employees}
        parentReviews={reviews}
        parentCertifications={certifications}
        onEmployeeChanged={() => loadData()}
      />

      <NewEmployeeModal
        open={newEmployeeModalOpen}
        onClose={() => setNewEmployeeModalOpen(false)}
        onCreated={() => loadData()}
        existingDepartments={departments}
      />
    </div>
  );
};

export default HrDirectoryInteractive;
